import { Badge, Button, EmptyState, Textarea, useToast } from '@orbind/ui'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { ExplorerBody, useExplorer, useExplorerConfig } from '../shell/explorer.tsx'
import {
  AVAILABILITIES,
  MY_STATUS,
  TEAM_STATUSES,
  derivePresence,
  findAvailability,
  type PersonStatus,
  type Presence,
  type StatusWorkItem,
} from '../mocks/status.ts'
import '../features/work-item/wi.css'
import './status.css'

const MAX_LENGTH = 180

/** O que o painel da direita mostra agora. */
type View = 'idle' | 'editing' | 'viewing'

/**
 * Ponto de presenca (derivado da atividade) + "z" de dormindo.
 *
 * Fora do horario o ponto some por completo — protótipo `_shell.html:2144`.
 * A cor nunca comunica sozinha: quem chama sempre imprime `presence.label`
 * ao lado.
 */
function PresenceDot({ presence, size = 'sm' }: { presence: Presence; size?: 'sm' | 'lg' }) {
  if (presence.hidden) return null
  return (
    <span
      className={`st-dot${size === 'lg' ? ' st-dot--lg' : ''}${presence.sleeping ? ' st-dot--z' : ''}`}
      style={{ background: presence.color }}
      title={presence.label}
      aria-hidden
    >
      {presence.sleeping ? 'z' : null}
    </span>
  )
}

function WorkItemRow({ item, onOpen }: { item: StatusWorkItem; onOpen: () => void }) {
  return (
    <button type="button" className="st-wi" onClick={onOpen}>
      <span className="st-wi__type" title={item.typeLabel}>
        <i className={item.typeIcon} aria-hidden />
      </span>
      <span className="ob-sr-only">{item.typeLabel}: </span>
      <span className="st-wi__code">{item.code}</span>
      <span className="st-wi__title">{item.title}</span>
      <span className="st-wi__time">{item.time}</span>
      <Badge tone={item.statusTone}>{item.statusLabel}</Badge>
    </button>
  )
}

function WorkSection({
  icon,
  iconColor,
  title,
  items,
  emptyText,
  onOpen,
}: {
  icon: string
  iconColor: string
  title: string
  items: readonly StatusWorkItem[]
  emptyText: string
  onOpen: (item: StatusWorkItem) => void
}) {
  return (
    <>
      <div className="st-sec">
        <i className={icon} aria-hidden style={{ fontSize: 13, color: iconColor }} />
        <h3 className="st-sec__title">{title}</h3>
        <span className="st-sec__count">
          {items.length} {items.length === 1 ? 'item' : 'itens'}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="st-sec__empty">{emptyText}</p>
      ) : (
        <div className="st-list">
          {items.map((item) => (
            <WorkItemRow key={item.id} item={item} onOpen={() => onOpen(item)} />
          ))}
        </div>
      )}
    </>
  )
}

/**
 * Status pessoal — publicar o que estou fazendo e ver o status da equipe
 * (regiões `showStatus`, `listStatus` e `statusViewing` do protótipo).
 *
 * A lista de pessoas mora no EXPLORER DO SHELL (`shell/explorer.tsx`): o
 * protótipo põe o explorer ao lado do rail, não dentro da tela. Aqui só
 * filtramos por `search` e devolvemos as linhas.
 *
 * Duas noções distintas convivem na tela:
 * - **disponibilidade**: o que a pessoa declarou (3 opções, `AVAILABILITIES`);
 * - **presença**: derivada da atividade na plataforma (`derivePresence`).
 *
 * Dados MOCADOS: publicar altera apenas o estado local desta tela.
 */
export function StatusPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const { search } = useExplorer()

  const [me, setMe] = useState<PersonStatus>(MY_STATUS)
  const [view, setView] = useState<View>('idle')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const [draft, setDraft] = useState(MY_STATUS.text ?? '')
  const [draftAvailability, setDraftAvailability] = useState(MY_STATUS.availabilityId)

  const query = search.trim().toLocaleLowerCase('pt-BR')
  const team = TEAM_STATUSES.filter(
    (person) =>
      person.name.toLocaleLowerCase('pt-BR').includes(query) ||
      person.area.toLocaleLowerCase('pt-BR').includes(query) ||
      (person.text ?? '').toLocaleLowerCase('pt-BR').includes(query),
  )

  const selected = selectedId === me.id ? me : (TEAM_STATUSES.find((person) => person.id === selectedId) ?? null)
  const myAvailability = findAvailability(me.availabilityId)

  const openEditor = () => {
    setDraft(me.text ?? '')
    setDraftAvailability(me.availabilityId)
    setSelectedId(null)
    setView('editing')
  }

  useExplorerConfig({
    footer: `${team.length} ${team.length === 1 ? 'pessoa com status' : 'pessoas com status'}`,
    onCreate: openEditor,
  })

  const publish = () => {
    const text = draft.trim()
    setMe((current) => ({
      ...current,
      text: text === '' ? null : text,
      availabilityId: draftAvailability,
      publishedAt: 'agora mesmo',
    }))
    setSelectedId(me.id)
    setView('viewing')
    toast.push({
      tone: 'success',
      message: `Status publicado · ${findAvailability(draftAvailability).label}. Todas as pessoas internas já veem a atualização.`,
    })
  }

  const clearStatus = () => {
    setMe((current) => ({ ...current, text: null, publishedAt: 'sem status publicado' }))
    setSelectedId(null)
    setView('idle')
    toast.push({ tone: 'info', message: 'Seu status foi removido do mural da equipe.' })
  }

  /** Protótipo `_shell.html:3388` — a linha abre o Item de Trabalho. */
  const openWorkItem = (item: StatusWorkItem) => navigate(`/work-items/${item.code}`)

  /**
   * Protótipo `_shell.html:3376` (`svMessage: () => this.openDm(...)`): abre a
   * conversa direta. Quem ainda não tem conversa cai na lista de Comunicação,
   * onde a conversa pode ser iniciada.
   */
  const sendMessage = (person: PersonStatus) =>
    navigate(person.dmChannelId ? `/channels/${person.dmChannelId}` : '/channels')

  const myLine = me.text ?? 'Você ainda não publicou um status — conte à equipe no que está trabalhando.'
  const minhaPresenca = derivePresence(me)

  /* ------------------------------------------------------ lista do explorer */

  const lista = (
    <>
      <button type="button" className={`st-me${view === 'editing' ? ' st-me--on' : ''}`} onClick={openEditor}>
        <span className="st-avatarwrap">
          <span className="st-avatar st-avatar--md">{me.initials}</span>
          <span className="st-dot st-dot--plus" aria-hidden>
            <i className="fas fa-plus" />
          </span>
        </span>
        <span className="st-mainline">
          <span className="st-mainline__title">Meu status</span>
          <span className="st-mainline__sub">{myLine}</span>
        </span>
      </button>

      <div className="st-sectiontitle">Recente</div>

      {team.map((person) => {
        const availability = findAvailability(person.availabilityId)
        const presence = derivePresence(person)
        return (
          <button
            key={person.id}
            type="button"
            className={`wi-row${person.id === selectedId ? ' wi-row--on' : ''}`}
            onClick={() => {
              setSelectedId(person.id)
              setView('viewing')
            }}
          >
            <span className="st-avatarwrap">
              <span className={`st-ring${person.text ? ' st-ring--on' : ''}`}>
                <span className="st-avatar st-avatar--sm">{person.initials}</span>
              </span>
              <PresenceDot presence={presence} />
            </span>
            <span className="wi-row__main">
              <span className="wi-row__top">
                <span className="wi-row__title">{person.name}</span>
              </span>
              <span className="wi-row__bottom">
                {/* o ponto nunca vem sozinho: disponibilidade declarada + presenca derivada */}
                <span className="wi-row__code">
                  {availability.label} · {presence.label}
                </span>
              </span>
            </span>
          </button>
        )
      })}

      {team.length === 0 ? (
        <div className="wi-empty" style={{ padding: '44px 24px' }}>
          <div className="wi-empty__icon">
            <i className="fas fa-user-friends" aria-hidden />
          </div>
          <span className="wi-empty__title">Ninguém encontrado</span>
          <span className="wi-empty__text">Nenhuma pessoa casa com a busca do explorer.</span>
        </div>
      ) : null}
    </>
  )

  /* -------------------------------------------------------------- conteudo */

  return (
    <>
      <ExplorerBody>{lista}</ExplorerBody>

      <div className="st-content">
        {view === 'editing' ? (
          <div className="st-card st-editor">
            <h2 className="st-editor__title">Atualizar meu status</h2>
            <p className="st-editor__hint">
              Seu status fica visível para todas as pessoas internas da empresa. Diga o que está fazendo agora e até
              quando.
            </p>

            <Textarea
              rows={3}
              maxLength={MAX_LENGTH}
              aria-label="O que você está fazendo agora"
              placeholder="Ex.: Em foco na triagem até as 12h"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />

            <div className="st-editor__grouplabel">Disponibilidade</div>
            <div className="st-avails" role="group" aria-label="Disponibilidade">
              {AVAILABILITIES.map((availability) => {
                const on = availability.id === draftAvailability
                return (
                  <button
                    key={availability.id}
                    type="button"
                    className={`st-avail${on ? ' st-avail--on' : ''}`}
                    aria-pressed={on}
                    onClick={() => setDraftAvailability(availability.id)}
                  >
                    <span
                      className="st-avail__dot"
                      style={
                        availability.color === 'transparent'
                          ? { background: 'transparent', border: '1.5px solid var(--ob-primary-border)' }
                          : { background: availability.color }
                      }
                      aria-hidden
                    />
                    {availability.label}
                  </button>
                )
              })}
            </div>
            <p className="st-avail__desc">{findAvailability(draftAvailability).description}</p>

            <div className="st-editor__footer">
              <span className="st-editor__counter">
                {draft.length}/{MAX_LENGTH} caracteres
              </span>
              {me.text ? (
                <Button variant="ghost" icon="fas fa-eraser" onClick={clearStatus}>
                  Remover meu status
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => setView('idle')}>
                Cancelar
              </Button>
              <Button variant="primary" disabled={draft.trim() === ''} onClick={publish}>
                Publicar status
              </Button>
            </div>
          </div>
        ) : view === 'viewing' && selected ? (
          <div className="st-profile">
            <span className="st-avatarwrap">
              <span className="st-ring st-ring--on st-ring--lg">
                <span className="st-avatar st-avatar--lg">{selected.initials}</span>
              </span>
              <PresenceDot presence={derivePresence(selected)} size="lg" />
            </span>

            <div>
              <div className="st-profile__name">{selected.id === me.id ? `${selected.name} (você)` : selected.name}</div>
              <div className="st-profile__meta">
                {selected.role} · {selected.area}
              </div>
              <div className="st-profile__meta" style={{ marginTop: 4 }}>
                <Badge
                  tone={findAvailability(selected.availabilityId).tone}
                  icon={findAvailability(selected.availabilityId).icon}
                >
                  {findAvailability(selected.availabilityId).label}
                </Badge>{' '}
                {selected.publishedAt}
              </div>
              <div className="st-profile__meta" style={{ marginTop: 4 }}>
                <i className="fas fa-signal" aria-hidden style={{ fontSize: 10, marginRight: 6 }} />
                {derivePresence(selected).label}
              </div>
            </div>

            {selected.text ? (
              <div className="st-card st-profile__quote">
                <p className="st-profile__quotetext">{selected.text}</p>
              </div>
            ) : (
              <div className="st-card st-profile__quote">
                <EmptyState
                  icon="far fa-dot-circle"
                  title="Sem status publicado"
                  description={
                    selected.id === me.id
                      ? 'Você ainda não contou à equipe no que está trabalhando.'
                      : `${selected.name} ainda não publicou um status hoje.`
                  }
                  action={
                    selected.id === me.id ? (
                      <Button variant="primary" icon="fas fa-plus" onClick={openEditor}>
                        Publicar meu status
                      </Button>
                    ) : undefined
                  }
                />
              </div>
            )}

            <div className="st-card st-profile__lists">
              <WorkSection
                icon="fas fa-play-circle"
                iconColor="var(--ob-primary)"
                title="Trabalhando agora"
                items={selected.working}
                emptyText="Nenhum Item de Trabalho em andamento no momento."
                onOpen={openWorkItem}
              />
              <WorkSection
                icon="fas fa-check-circle"
                iconColor="var(--ob-success)"
                title="Concluídos hoje"
                items={selected.doneToday}
                emptyText="Nada concluído hoje ainda."
                onOpen={openWorkItem}
              />
              <WorkSection
                icon="far fa-calendar-check"
                iconColor="var(--ob-primary)"
                title="Programados para hoje"
                items={selected.scheduledToday}
                emptyText="Nenhum Item de Trabalho programado para hoje."
                onOpen={openWorkItem}
              />
            </div>

            {selected.id === me.id ? (
              <Button variant="secondary" icon="fas fa-pen" onClick={openEditor}>
                Editar meu status
              </Button>
            ) : (
              <Button variant="secondary" icon="fas fa-comment" onClick={() => sendMessage(selected)}>
                Enviar mensagem
              </Button>
            )}
          </div>
        ) : (
          <div className="st-team">
            {me.text === null ? (
              <div className="st-invite">
                <span className="st-invite__icon">
                  <i className="far fa-dot-circle" aria-hidden style={{ fontSize: 18 }} />
                </span>
                <span className="st-invite__text">
                  <span className="st-invite__title">Compartilhe atualizações de status</span>
                  <p className="st-invite__desc">
                    Conte à empresa no que você está focado agora. Leva menos de um minuto e evita interrupções.
                  </p>
                </span>
                <Button variant="primary" icon="fas fa-plus" onClick={openEditor}>
                  Publicar meu status
                </Button>
              </div>
            ) : (
              <div className="st-invite">
                <span className="st-invite__icon">
                  <i className={myAvailability.icon} aria-hidden style={{ fontSize: 18 }} />
                </span>
                <span className="st-invite__text">
                  <span className="st-invite__title">
                    Seu status · {myAvailability.label} · {me.publishedAt}
                  </span>
                  <p className="st-invite__desc">
                    {me.text}
                    <br />
                    <span style={{ fontSize: 11.5 }}>Presença: {minhaPresenca.label}</span>
                  </p>
                </span>
                <Button variant="secondary" icon="fas fa-pen" onClick={openEditor}>
                  Atualizar
                </Button>
              </div>
            )}

            <div>
              <h2 className="st-team__title">Status do time</h2>
              <p className="st-team__desc">
                Clique em uma pessoa para ver o status completo e no que ela está trabalhando.
              </p>
            </div>

            {team.length === 0 ? (
              <div className="st-card st-placeholder">
                <EmptyState
                  icon="fas fa-user-friends"
                  title="Nenhuma pessoa encontrada"
                  description="Ninguém casa com a busca do explorer. Limpe a busca para ver todo o time novamente."
                />
              </div>
            ) : (
              <div className="st-team__grid">
                {team.map((person) => {
                  const availability = findAvailability(person.availabilityId)
                  const presence = derivePresence(person)
                  return (
                    <button
                      key={person.id}
                      type="button"
                      className="st-person"
                      title={`${person.name} · ${availability.label} · ${presence.label}`}
                      onClick={() => {
                        setSelectedId(person.id)
                        setView('viewing')
                      }}
                    >
                      <span className="st-avatarwrap">
                        <span className={`st-ring${person.text ? ' st-ring--on' : ''}`}>
                          <span className="st-avatar st-avatar--md">{person.initials}</span>
                        </span>
                        <PresenceDot presence={presence} />
                      </span>
                      <span className="st-person__name">{person.name}</span>
                      <span className="st-person__hint">{availability.label}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
